import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { DICT } from "@/lib/i18n";

const StoreCtx = createContext(null);
const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function StoreProvider({ children }) {
  const [lang, setLang] = useState(() => load("cb_lang", "en"));
  const [cart, setCart] = useState(() => load("cb_cart", []));
  const [wishlist, setWishlist] = useState(() => load("cb_wish", []));
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => { localStorage.setItem("cb_lang", JSON.stringify(lang)); document.documentElement.lang = lang; }, [lang]);
  useEffect(() => { localStorage.setItem("cb_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("cb_wish", JSON.stringify(wishlist)); }, [wishlist]);

  const t = useCallback((k, vars) => {
    let s = DICT[lang][k] ?? DICT.en[k] ?? k;
    if (vars && typeof s === "string") Object.entries(vars).forEach(([a, b]) => { s = s.replace(`{${a}}`, b); });
    return s;
  }, [lang]);

  const addToCart = useCallback((p, size, qty = 1) => {
    const stock = p.sizes?.find((s) => s.size === size)?.stock ?? 0;
    setCart((c) => {
      const i = c.findIndex((x) => x.product_id === p.id && x.size === size);
      if (i >= 0) {
        const n = [...c];
        n[i] = { ...n[i], qty: Math.min(n[i].qty + qty, stock, 10) };
        return n;
      }
      return [...c, { product_id: p.id, slug: p.slug, name: p.name, brand: p.brand, image: p.images?.[0], size, qty, price: p.final_price, stock }];
    });
    toast.success(DICT[lang].added, { description: `${p.name} · EU ${size}` });
    setCartOpen(true);
  }, [lang]);

  const updateQty = useCallback((pid, size, qty) => setCart((c) => c.map((x) => (x.product_id === pid && x.size === size
    ? { ...x, qty: Math.max(1, Math.min(qty, x.stock || 10, 10)) } : x))), []);
  const changeSize = useCallback((pid, oldSize, newSize, stock) => setCart((c) => {
    if (c.some((x) => x.product_id === pid && x.size === newSize)) return c;
    return c.map((x) => (x.product_id === pid && x.size === oldSize ? { ...x, size: newSize, stock, qty: Math.min(x.qty, stock) } : x));
  }), []);
  const removeItem = useCallback((pid, size) => setCart((c) => c.filter((x) => !(x.product_id === pid && x.size === size))), []);
  const clearCart = useCallback(() => setCart([]), []);
  const toggleWish = useCallback((p) => setWishlist((w) => (w.some((x) => x.id === p.id) ? w.filter((x) => x.id !== p.id)
    : [...w, { id: p.id, slug: p.slug, name: p.name, brand: p.brand, images: p.images, final_price: p.final_price, price: p.price, on_sale: p.on_sale, sizes: p.sizes, is_demo: p.is_demo }])), []);
  const isWished = useCallback((id) => wishlist.some((x) => x.id === id), [wishlist]);

  const count = cart.reduce((a, b) => a + b.qty, 0);
  const subtotal = cart.reduce((a, b) => a + b.qty * b.price, 0);

  const value = useMemo(() => ({ lang, setLang, t, cart, addToCart, updateQty, changeSize, removeItem, clearCart, count, subtotal,
    wishlist, toggleWish, isWished, cartOpen, setCartOpen }), [lang, t, cart, addToCart, updateQty, changeSize, removeItem, clearCart,
    count, subtotal, wishlist, toggleWish, isWished, cartOpen]);
  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}

export const useStore = () => useContext(StoreCtx);
